// Shared "click flash" beat visual, used identically by the standalone
// Metronome tab and Performance mode — factored out here so both stay in
// lockstep instead of drifting into two slightly different implementations.
//
// Each beat is one circle, small/dim at rest. Every audible tick (main beat,
// "and", or a sixteenth) jumps the circle straight to a peak size + color —
// a hard cut, no transition — holds it there for a short, tempo-relative
// duration, then jumps straight back to idle, also a hard cut. Deliberately
// no easing in either direction: this is meant to feel punchy and jerky,
// like a physical click light, not a smooth animated pulse. The peak SIZE
// tells you what kind of tick just happened (100% main beat, ~80% the
// "and", ~60% a sixteenth's "e"/"a") — same idea a drummer already reads
// off a real metronome's accent pattern, just flashed instead of switched
// on/off.

import type { BeatEvent } from '../audio/MetronomeEngine';

export interface BeatVisualEls {
  /** One per main beat (beatsPerMeasure of them), 0-indexed. */
  pills: HTMLDivElement[];
  /** The label span inside each pill, parallel to `pills` — shows that
   * beat's own number ("1", "2", …) for the ENTIRE duration that beat is
   * current (not just its brief flash — see triggerPulse), and is blank the
   * rest of the time, including during that same beat's own "and"/sixteenth
   * subdivision ticks. Only one label is ever showing at once, so reading
   * the row is "1 ... 2 ... 3 ... 4 ..." at the pace of the beat itself,
   * not a rapid flicker of every subdivision syllable. A real child
   * element, not styled via the pill's own transform, so the label stays a
   * constant, readable size whether the pill is sitting at idle or peak
   * scale. */
  labels: HTMLSpanElement[];
}

/** Which of the three pulse tiers a given subdivision slot belongs to,
 * given how many subdivisions-per-beat are currently configured
 * (MetronomeSettings.subdivision: 1, 2, or 4) and which slot within that
 * this particular tick is (BeatEvent.subdivisionIndex). Follows standard
 * "1 e & a" sixteenth-note counting emphasis: the beat itself is always
 * strongest; with 8ths on, the single "and" is the medium tier; with 16ths
 * on, only the "&" (the tick that ALSO aligns with the 8th-note and, i.e.
 * subdivisionIndex 2 of 4) gets that medium tier — the off-8th "e" and "a"
 * ticks (indices 1 and 3) are the weakest tier, same as a real drummer
 * would count them. */
export type PulseTier = 'beat' | 'and' | 'sixteenth';

export function pulseTierFor(subdivisionIndex: number, subdivisionCount: number): PulseTier {
  if (subdivisionIndex === 0) return 'beat';
  if (subdivisionCount === 2) return 'and';
  // subdivisionCount === 4: "1 e & a" — index 2 is the "&" (medium tier),
  // indices 1 and 3 ("e" and "a") are the weakest tier.
  return subdivisionIndex === 2 ? 'and' : 'sixteenth';
}

const PULSE_SCALE: Record<PulseTier, number> = { beat: 1.3, and: 1.04, sixteenth: 0.81 };
/** Peak fill color per tier — brightest/warmest gold at the beat, cooling
 * toward the accent blue family as the tier gets weaker (same "bright vs.
 * darker, one palette" logic the earlier split-pill design used, just
 * applied as three tiers of one continuous pulse instead of two fixed
 * halves). Kept as plain color strings (not CSS var() lookups) so
 * buildGlow below can interpolate an rgba() glow color to match without
 * needing to parse a custom property's computed value at runtime. */
const PULSE_COLOR: Record<PulseTier, string> = {
  beat: 'color-mix(in srgb, #d9a25c 70%, #ffcf4d)', // brighter gold than --warn's own muted amber
  and: '#7c8fd1', // the app's one accent blue
  sixteenth: 'color-mix(in srgb, #7c8fd1 55%, black)', // a darker shade of that same blue
};
const PULSE_GLOW: Record<PulseTier, string> = {
  beat: '0 0 clamp(14px, 7cqh, 40px) rgba(255, 200, 80, 0.6)',
  and: '0 0 clamp(10px, 5cqh, 28px) rgba(124, 143, 209, 0.5)',
  sixteenth: '0 0 clamp(6px, 3cqh, 18px) rgba(124, 143, 209, 0.35)',
};

/** Builds (or rebuilds) the row of beat circles inside `container`,
 * replacing whatever was there before. Every circle starts idle (small,
 * dim) — nothing about the shape hints at which pulse tiers are currently
 * reachable; that's driven entirely by which BeatEvents actually get
 * scheduled (see triggerPulse below), so toggling the song/metronome's
 * subdivision setting never changes the row's layout, only which tiers
 * ever fire. */
export function renderBeatVisual(container: HTMLElement, beatsPerMeasure: number, accentFirst: boolean): BeatVisualEls {
  container.innerHTML = '';
  const pills: HTMLDivElement[] = [];
  const labels: HTMLSpanElement[] = [];

  for (let i = 0; i < beatsPerMeasure; i++) {
    const pill = document.createElement('div');
    pill.className = 'beat-pill' + (i === 0 && accentFirst ? ' beat-accent' : '');
    const label = document.createElement('span');
    label.className = 'beat-pill-label';
    label.textContent = String(i + 1);
    pill.appendChild(label);
    container.appendChild(pill);
    pills.push(pill);
    labels.push(label);
  }

  return { pills, labels };
}

/** Fraction of `tickGapMs` (the time until the NEXT tick) that the peak
 * flash holds before hard-cutting back to idle. Tempo-relative rather than
 * a fixed duration so the hold always reads as "a portion of this beat",
 * not a fixed-length flash that would swallow almost the entire gap at
 * fast tempos/fine subdivisions, or look like a barely-there blip at slow
 * ones. */
const PEAK_HOLD_FRACTION = 0.18;
/** Floor so even an extremely fast tick (e.g. 300bpm sixteenths) still
 * holds long enough to actually paint before reverting — see the note in
 * triggerPulse below about requestAnimationFrame timing. */
const MIN_HOLD_MS = 20;

/** Triggers one flash on the circle for `beatIndexInMeasure`, sized/colored
 * for the tier `subdivisionIndex`/`subdivisionCount` resolve to (via
 * pulseTierFor), holding at peak for a fraction of `tickGapMs` (the time
 * until the NEXT tick — see PEAK_HOLD_FRACTION) before hard-cutting back to
 * idle. Every property change here is instant (no CSS transition — see
 * style.css's .beat-pill rule), so the shape is always either sitting at
 * peak or sitting at idle, never mid-animation — the punchy, jerky "click"
 * feel this replaces the earlier eased heartbeat-pulse design for.
 *
 * On a main beat (subdivisionIndex 0) only, the pill's label is set to that
 * beat's own number and LEFT showing — not cleared on the short flash
 * timeout below, but on whichever pill fires the NEXT main beat, so exactly
 * one label is visible for the beat's entire duration ("1 ... 2 ... 3 ...
 * 4 ..." at the pace of the beat, not a rapid per-subdivision flicker).
 * "And"/sixteenth ticks (subdivisionIndex > 0) never touch any label at
 * all — they only flash the circle's size/color.
 *
 * `isDownbeat` (only meaningful for tier 'beat') applies the same subtle
 * extra-emphasis overshoot the earlier design gave beat 1 — a bigger scale
 * on the SAME peak color, never a different hue. Silently does nothing for
 * a beatIndexInMeasure with no matching pill (e.g. a beatsPerMeasure
 * mismatch mid-flight) rather than throwing, since a settings change
 * mid-flight is a normal, harmless race, not a bug. */
export function triggerPulse(
  els: BeatVisualEls,
  beatIndexInMeasure: number,
  subdivisionIndex: number,
  subdivisionCount: number,
  tickGapMs: number,
  isDownbeat = false,
): void {
  const pill = els.pills[beatIndexInMeasure];
  if (!pill) return;

  const tier = pulseTierFor(subdivisionIndex, subdivisionCount);
  const scale = PULSE_SCALE[tier] * (isDownbeat && tier === 'beat' ? 1.08 : 1);
  pill.style.setProperty('--beat-pulse-scale', String(scale));
  pill.style.setProperty('--beat-pulse-color', PULSE_COLOR[tier]);
  pill.style.setProperty('--beat-pulse-glow', PULSE_GLOW[tier]);

  if (subdivisionIndex === 0) {
    els.labels.forEach((label, i) => label.classList.toggle('is-active', i === beatIndexInMeasure));
  }

  const holdMs = Math.max(MIN_HOLD_MS, tickGapMs * PEAK_HOLD_FRACTION);
  setTimeout(() => {
    pill.style.setProperty('--beat-pulse-scale', 'var(--beat-pulse-idle-scale)');
    pill.style.setProperty('--beat-pulse-color', 'var(--bg-panel-raised-alt)');
    pill.style.setProperty('--beat-pulse-glow', 'none');
  }, holdMs);
}

/** Schedules a pulse for a single beat/subdivision event so it lands
 * exactly when the click is actually audible, not the instant the event
 * fires. MetronomeEngine schedules audio up to 0.2s AHEAD of when it plays
 * (the standard lookahead-scheduler pattern — see its own doc comment) and
 * emits this event at schedule time, not at the moment the sound starts —
 * pulsing immediately would show the beat before you hear it, up to a very
 * noticeable 200ms early. `audioContext` is required so the delay is
 * computed against the SAME clock the audio itself is scheduled against
 * (see SyntheticClickLayer.currentFileTime's doc comment for the general
 * principle: never mix AudioContext time with a different clock).
 * `secondsPerTick` (the gap until the NEXT subdivision tick, at the
 * current tempo/subdivision setting) drives the flash's peak-hold duration
 * — passed in rather than recomputed here since callers already have the
 * tempo math on hand (BeatEvent itself doesn't carry BPM). */
export function scheduleBeatPulse(
  els: BeatVisualEls,
  beat: BeatEvent,
  subdivisionCount: number,
  secondsPerTick: number,
  audioContext: AudioContext,
  isDownbeat = false,
): void {
  const delayMs = Math.max(0, (beat.scheduledTime - audioContext.currentTime) * 1000);
  setTimeout(
    () => triggerPulse(els, beat.beatIndexInMeasure, beat.subdivisionIndex, subdivisionCount, secondsPerTick * 1000, isDownbeat),
    delayMs,
  );
}
